import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { AddIcon } from '../../../../shared/ui/icons/add-icon/add-icon';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { ScopeResolver, Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { Loading } from '../../../../utils/loading/loading';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import {
  Department,
  DepartmentError,
  DepartmentListFilters,
  DepartmentListResponse,
  DepartmentSortChange,
  DepartmentVTO,
  GetDepartmentsQueryParams,
} from '../../../../pages/admin-dashboard/departments/departments.model';
import { DepartmentService } from '../../../../services/department/department.service';
import { DepartmentsListFilter } from './departments-list-filter/departments-list-filter';
import { toDepartmentTableRows } from './departments-list.mapper';

const DEFAULT_FILTERS: DepartmentListFilters = {
  departmentName: '',
  createdOnFrom: '',
  createdOnTo: '',
  lastModifiedOnFrom: '',
  lastModifiedOnTo: '',
  orderBy: 'LAST_MODIFICATION_DATE',
  orderDir: 'DESC',
};

const NOTIFICATION_DURATION = 3000;
const DEFAULT_PAGE_SIZE = 25;

const ACTION_EDIT = 1;
const ACTION_DELETE = 2;
const ACTION_VIEW = 3;

@Component({
  selector: 'app-departments-list-component',
  imports: [
    RouterLink,
    AddIcon,
    HasScopeDirective,
    ConfirmationDialog,
    Pagination,
    Tables,
    ErrorData,
    Loading,
    SuccessfulNotification,
    DepartmentsListFilter,
  ],
  templateUrl: './departments-list-component.html',
  styleUrl: './departments-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentsListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private departmentService = inject(DepartmentService);

  private successTimeout?: ReturnType<typeof setTimeout>;

  readonly SCOPES = SCOPES;
  readonly isArabic = computed(() => this.locale.startsWith('ar'));

  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(DEFAULT_PAGE_SIZE);
  totalItems = signal<number>(0);

  filters = signal<DepartmentListFilters>({ ...DEFAULT_FILTERS });
  appliedFilters = signal<DepartmentListFilters>({ ...DEFAULT_FILTERS });

  allDepartments = signal<Department[]>([]);
  loading = signal<boolean>(false);
  errorGettingDepartments = signal<string | null>(null);

  departmentToDelete = signal<Department | null>(null);
  deleteLoading = signal<boolean>(false);
  deleteError = signal<string>('');

  showSuccessMessage = signal<boolean>(false);
  deletedDepartmentName = signal<string>('');

  readonly isDeleteDialogOpen = computed(() => this.departmentToDelete() !== null);

  readonly isSearchApplied = computed(() => !!this.appliedFilters().departmentName);

  readonly areDateFiltersApplied = computed(() => {
    const f = this.appliedFilters();
    return !!(f.createdOnFrom || f.createdOnTo || f.lastModifiedOnFrom || f.lastModifiedOnTo);
  });

  readonly isFiltersApplied = computed(() => this.isSearchApplied() || this.areDateFiltersApplied());

  readonly emptyFilterTitle = computed<string>(() =>
    this.areDateFiltersApplied()
      ? $localize`:@@departmentEmptyFilters:No departments match your selected filters.`
      : $localize`:@@departmentEmptySearch:No departments match your search.`,
  );

  readonly emptyContentTitle = $localize`:@@departmentEmptyList:No departments found.`;

  readonly emptyContentDescription = $localize`:@@departmentEmptyListDescription:Click "Create New Department" to add the first department.`;

  readonly tableHeaders = computed(() => [
    { key: 'rowNumber', label: '#' },
    { key: 'title', label: this.isArabic() ? 'العنوان' : 'Title' },
    { key: 'createdOnBy', label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created On / By' },
    {
      key: 'lastModifiedOnBy',
      label: this.isArabic() ? 'آخر تعديل / بواسطة' : 'Last Modified On / By',
    },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  readonly tableActions = computed<ActionInterface[]>(() => [
    { id: ACTION_VIEW, titleEn: 'View Department Details', titleAr: 'عرض تفاصيل الإدارة' } as ActionInterface,
    { id: ACTION_EDIT, titleEn: 'Edit', titleAr: 'تعديل' } as ActionInterface,
    { id: ACTION_DELETE, titleEn: 'Delete', titleAr: 'حذف' } as ActionInterface,
  ]);

  readonly departmentScopeResolver: ScopeResolver = (action) => {
    switch (action.id) {
      case ACTION_VIEW:
        return SCOPES.ROLES.READ;
      case ACTION_EDIT:
        return SCOPES.ROLES.UPDATE;
      case ACTION_DELETE:
        return SCOPES.ROLES.DELETE;
      default:
        return null;
    }
  };

  readonly tableRows = computed(() =>
    toDepartmentTableRows(this.allDepartments(), (this.currentPage() - 1) * this.itemsPerPage(), this.isArabic()),
  );

  readonly totalPages = computed<number>(() => Math.ceil(this.totalItems() / this.itemsPerPage()));

  readonly startItem = computed<number>(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  readonly endItem = computed<number>(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  readonly getPageNumbers = computed<number[]>(() => {
    const pages: number[] = [];
    const total = this.totalPages();
    const current = this.currentPage();

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push(-1);
      if (current > 2) pages.push(current - 1);
      if (current !== 1 && current !== total) pages.push(current);
      if (current < total - 1) pages.push(current + 1);
      if (current < total - 2) pages.push(-1);
      pages.push(total);
    }

    return pages;
  });

  readonly deleteDialogItemName = computed<string>(() => {
    const department = this.departmentToDelete();
    if (!department) return '';
    return this.isArabic() ? department.titleAr : department.titleEn;
  });

  ngOnInit(): void {
    this.loadDepartments();
  }

  ngOnDestroy(): void {
    if (this.successTimeout) clearTimeout(this.successTimeout);
  }

  loadDepartments(): void {
    this.loading.set(true);
    this.errorGettingDepartments.set(null);

    this.departmentService
      .getDepartments(this.buildQueryParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: DepartmentListResponse) => {
          this.allDepartments.set(this.mapDepartmentsForView(response.list ?? []));
          this.totalItems.set(response.total ?? 0);
          this.loading.set(false);
        },
        error: (err: DepartmentError) => {
          this.loading.set(false);
          this.errorGettingDepartments.set(
            this.isArabic()
              ? err?.messageAr || 'حدث خطأ أثناء تحميل الإدارات'
              : err?.messageEn || 'Error loading departments',
          );
        },
      });
  }

  private buildQueryParams(): GetDepartmentsQueryParams {
    const filters = this.appliedFilters();

    return {
      quickSearch: filters.departmentName || undefined,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
      orderBy: filters.orderBy,
      orderDir: filters.orderDir,
      createdOnFrom: filters.createdOnFrom || undefined,
      createdOnTo: filters.createdOnTo || undefined,
      lastModifiedOnFrom: filters.lastModifiedOnFrom || undefined,
      lastModifiedOnTo: filters.lastModifiedOnTo || undefined,
    };
  }

  private mapDepartmentsForView(departments: DepartmentVTO[]): Department[] {
    return departments.map((d) => ({
      id: d.id,
      titleAr: d.titleAr,
      titleEn: d.titleEn,
      createdOn: d.createdOn,
      createdBy: d.createdBy?.fullName ?? '',
      lastModifiedOn: d.lastModifiedOn,
      lastModifiedBy: d.lastModifiedBy?.fullName ?? null,
    }));
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.currentPage.set(page);
      this.loadDepartments();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadDepartments();
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadDepartments();
    }
  }

  onPageSizeChange(newSize: number): void {
    this.itemsPerPage.set(Number(newSize));
    this.currentPage.set(1);
    this.loadDepartments();
  }

  onApplyFilters(newFilters: DepartmentListFilters): void {
    this.filters.set(newFilters);
    this.appliedFilters.set({ ...newFilters });
    this.currentPage.set(1);
    this.loadDepartments();
  }

  onResetFilters(): void {
    const { orderBy, orderDir } = this.filters();
    const reset = { ...DEFAULT_FILTERS, orderBy, orderDir };
    this.filters.set(reset);
    this.appliedFilters.set({ ...reset });
    this.currentPage.set(1);
    this.loadDepartments();
  }

  onSortChange(sort: DepartmentSortChange): void {
    this.filters.update((f) => ({ ...f, ...sort }));
    this.appliedFilters.update((f) => ({ ...f, ...sort }));
    this.currentPage.set(1);
    this.loadDepartments();
  }

  onActionTriggered(payload: { event: Event; actionId: number; entity: any }): void {
    const department = payload.entity._original as Department;

    if (payload.actionId === ACTION_VIEW) {
      this.router.navigate(['/admin/departments/view-department', department.id]);
    } else if (payload.actionId === ACTION_EDIT) {
      this.onEditDepartment(department.id);
    } else if (payload.actionId === ACTION_DELETE) {
      this.openDeleteDialog(department);
    }
  }

  onEditDepartment(departmentId: number): void {
    this.router.navigate(['/admin/departments/edit-department', departmentId]);
  }

  openDeleteDialog(department: Department): void {
    this.deleteError.set('');
    this.departmentToDelete.set(department);
  }

  onCancelDelete(): void {
    if (this.deleteLoading()) return;
    this.closeDeleteDialog();
  }

  closeDeleteDialog(): void {
    this.departmentToDelete.set(null);
    this.deleteError.set('');
  }

  onDeleteDialogSubmit(): void {
    const department = this.departmentToDelete();
    if (!department || this.deleteLoading()) return;

    this.deleteLoading.set(true);
    this.deleteError.set('');

    this.departmentService
      .deleteDepartment(department.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.handleDeleteSuccess(department),
        error: (err: DepartmentError) => {
          this.deleteLoading.set(false);
          this.deleteError.set(
            this.isArabic()
              ? err?.messageAr || 'حدث خطأ أثناء حذف الإدارة'
              : err?.messageEn || 'Error deleting department',
          );
        },
      });
  }

  private handleDeleteSuccess(department: Department): void {
    this.deleteLoading.set(false);
    this.closeDeleteDialog();

    this.deletedDepartmentName.set(this.isArabic() ? department.titleAr : department.titleEn);
    this.displaySuccessMessage();

    const isLastItemOnPage = this.allDepartments().length === 1;

    this.allDepartments.update((list) => list.filter((d) => d.id !== department.id));
    this.totalItems.update((total) => Math.max(0, total - 1));

    if (isLastItemOnPage && this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadDepartments();
    }
  }

  private displaySuccessMessage(): void {
    this.showSuccessMessage.set(true);

    if (this.successTimeout) clearTimeout(this.successTimeout);
    this.successTimeout = setTimeout(() => {
      this.showSuccessMessage.set(false);
    }, NOTIFICATION_DURATION);
  }
}
